import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { SlideTransition } from '../../Transition/Transition';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Box,
  Typography,
  TextField,
  useMediaQuery
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import './QuickNotesRawDialog.scss';
import { scrollAndFocusTextareaEnd } from '../../../utils/dom';
import RecordMeetingRemarkButton from '../../RecordMeetingRemarkButton/RecordMeetingRemarkButton';
import { getRemarkDateLine } from '../../../utils/client';
import { shouldEnableSpeechToText } from '../../../utils/user';
import { GPTProductRecommendationButton } from '../ChatGPTPresetDrawer/GPTProductRecommendation/GPTProductRecommendation';
import CampaignCodeControl from '../../CampaignCodeControl/CampaignCodeControl';
import { ChatGPTPersonalAnalysisTextButton } from '../ChatGPTPresetDrawer/ChatGPTPersonalAnalysis/ChatGPTPersonalAnalysis';
import { getLatestRemark } from '../../../utils/remarks/get-latest-remark';
import { useWindowSize } from '../../../utils/hooks';
import FileText from 'lucide-react/dist/esm/icons/file-text';

const useStyles = makeStyles()(theme => ({
  dialogPaper: {
    margin: '16px !important',
    width: 'calc(100% - 32px) !important',
    maxHeight: 'calc(100% - 32px) !important',
    [theme.breakpoints.up('md')]: {
      maxWidth: '900px !important'
    },
    [theme.breakpoints.down('sm')]: {
      margin: '8px !important',
      width: 'calc(100% - 16px) !important',
      maxHeight: 'calc(100% - 16px) !important'
    }
  },
  notionContainer: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    overflow: 'hidden',
    boxSizing: 'border-box',
    height: '100%',
    '&:hover': {
      borderColor: '#cbd5e1'
    }
  },
  notionContainerFocused: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}1a` // 1a is 10% opacity in hex
  },
  headerToolbar: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '6px 12px',
    borderBottom: '1px solid #f1f5f9',
    backgroundColor: '#ffffff',
    userSelect: 'none'
  },
  headerTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#475569',
    lineHeight: '28px'
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    marginLeft: '12px'
  },
  actionButton: {
    padding: '0px 6px',
    fontSize: '12px',
    minWidth: 'unset',
    textTransform: 'none',
    fontWeight: 500,
    margin: 0,
    '&:hover': {
      backgroundColor: '#f1f5f9'
    }
  },
  textareaField: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fffbeb',
    '& .MuiInputBase-root': {
      padding: '10px 12px',
      fontSize: '15.5px',
      lineHeight: '1.6',
      color: '#451a03',
      alignItems: 'flex-start',
      height: '100%',
      flexGrow: 1,
      boxSizing: 'border-box'
    },
    '& textarea': {
      height: '100% !important',
      overflowY: 'auto !important',
      color: '#451a03'
    }
  }
}));

const QuickNotesRawDialog = props => {
  const {
    intl,
    userDetails,
    teamCampaignCodes,
    client,
    remark,
    open,
    onClose,
    onExited,
    onSubmit,
    toggleControl,
    userSubscription,
    isRecruitment,
    ...rest
  } = props;
  const [internalRemark, setInternalRemark] = useState(remark);
  const shouldFullScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const inputRef = useRef(null);
  const gptRecommendationRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const { classes } = useStyles();

  const [innerWidth] = useWindowSize();
  const isWidescreen = innerWidth > 800;

  const enableSpeechToText = shouldEnableSpeechToText({
    user: userDetails,
    teamCampaignCodes: teamCampaignCodes,
    userSubscription: userSubscription
  });

  const onChange = event => setInternalRemark(event.target.value);

  const appendRemark = text => {
    setInternalRemark(
      `${internalRemark}${internalRemark ? `\n${!(internalRemark || '').endsWith('\n') ? '\n' : ''}` : ''}${text}`
    );
  };

  const submit = () => {
    if (onSubmit) {
      onSubmit(internalRemark);
    }
    onClose();
  };

  const addDate = () => {
    appendRemark(getRemarkDateLine());

    setTimeout(() => {
      if (inputRef.current) {
        const textareaElem = inputRef.current.querySelector('textarea');
        scrollAndFocusTextareaEnd(textareaElem);
      }
    }, 150);
  };

  useEffect(() => {
    setInternalRemark(remark);

    return () => {
      setInternalRemark('');
    };
  }, [remark]);

  return (
    <Dialog
      data-body-scroll-lock-ignore="true"
      className="quick-notes-dialog"
      open={open}
      onClose={onClose}
      fullWidth={true}
      fullScreen={shouldFullScreen}
      maxWidth={isWidescreen ? 'md' : 'sm'}
      {...rest}
      slots={{
        transition: SlideTransition
      }}
      slotProps={{
        transition: {
          onExited
        },
        paper: {
          className: classes.dialogPaper,
          style: !shouldFullScreen
            ? {
                height: '80%'
              }
            : undefined
        }
      }}
    >
      <DialogContent style={{ padding: '20px 24px 8px 24px', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box className={`${classes.notionContainer} ${focused ? classes.notionContainerFocused : ''}`}>
          {/* Header Toolbar */}
          <Box className={classes.headerToolbar} onClick={event => event.preventDefault()}>
            <Box style={{ display: 'flex', alignItems: 'center', flexGrow: 1, flexWrap: 'wrap', gap: '8px' }}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '4px' }}>
                <FileText size={16} style={{ color: '#d97706', verticalAlign: 'middle' }} />
                <Typography className={classes.headerTitle} style={{ margin: 0 }}>
                  {intl.formatMessage({ id: 'Quick Notes' })}
                </Typography>
              </Box>
              <Button
                color="primary"
                variant="outlined"
                size="small"
                onClick={addDate}
                className={classes.actionButton}
              >
                {intl.formatMessage({ id: 'add-date' })}
              </Button>

              {enableSpeechToText && (
                <RecordMeetingRemarkButton
                  ButtonProps={{
                    color: 'primary',
                    size: 'small',
                    style: { pointerEvents: 'auto' },
                    className: classes.actionButton
                  }}
                  IconProps={{
                    style: { fontSize: 16 }
                  }}
                  onResult={result => appendRemark(`${getRemarkDateLine()}${result}`)}
                />
              )}

              {enableSpeechToText && isRecruitment && (
                <CampaignCodeControl targetCampaignCode={['FEATURE_RECRUIT']}>
                  <RecordMeetingRemarkButton
                    ButtonProps={{
                      color: 'primary',
                      size: 'small',
                      style: { pointerEvents: 'auto' },
                      className: classes.actionButton
                    }}
                    IconProps={{
                      style: { fontSize: 16 }
                    }}
                    onResult={result => appendRemark(`${getRemarkDateLine()}${result}`)}
                    isRecruit={true}
                  />
                </CampaignCodeControl>
              )}

              <CampaignCodeControl targetCampaignCode={['FEATURE_PRODUCT_RECOMMENDATION_CHUBB', 'FEATURE_GPT']}>
                {client && (
                  <GPTProductRecommendationButton
                    ref={gptRecommendationRef}
                    useLoadingBackdrop={true}
                    toggleControl={toggleControl}
                    client={client}
                    language={intl.locale}
                  >
                    <Button
                      onClick={() => gptRecommendationRef.current.handleCheckAndSend?.()}
                      color="primary"
                      variant="outlined"
                      size="small"
                      className={classes.actionButton}
                    >
                      <img
                        style={{
                          width: '12px',
                          height: '12px',
                          paddingRight: '2px'
                        }}
                        src="/img/icons/ai_bg_free.svg"
                        alt=""
                      />
                      {intl.formatMessage({
                        id: 'gpt-product-recommendation-client-entry-title'
                      })}
                    </Button>
                  </GPTProductRecommendationButton>
                )}
              </CampaignCodeControl>

              <CampaignCodeControl targetCampaignCode={['FEATURE_GPT']}>
                <ChatGPTPersonalAnalysisTextButton
                  intl={intl}
                  size="small"
                  style={{
                    padding: '0px 4px',
                    fontSize: '12px',
                    minWidth: 'unset',
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                  onClick={() => {
                    toggleControl('ChatGPTPersonalAnalysis', {
                      client: client,
                      type: 'meeting_notes',
                      open: true,
                      payload: { remark: getLatestRemark(internalRemark) }
                    });
                  }}
                />
              </CampaignCodeControl>
            </Box>
          </Box>

          {/* Text Area */}
          <TextField
            variant="standard"
            fullWidth
            multiline
            value={internalRemark}
            onChange={onChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            slotProps={{
              input: {
                disableUnderline: true
              }
            }}
            className={classes.textareaField}
            ref={inputRef}
          />
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '8px 24px 16px 24px' }}>
        <Button onClick={submit} color="primary" autoFocus>
          {intl.formatMessage({ id: 'Close' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const container = connect(
  state => ({
    userDetails: state.user.userDetails,
    teamCampaignCodes: state.functions.campaignCodes,
    userSubscription: state.userSubscription.userSubscription
  }),
  {}
)(QuickNotesRawDialog);

export default injectIntl(container);
